import { HttpClient, HttpContext, HttpHeaders } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import {
  SalesLevelRateEntryDto,
  TranslationsCustomTemplatesService,
  UsersTenantResponseDto,
  UsersTenantsService,
} from "@o2-fe/api";
import {
  SUPPRESS_ERROR_TOAST,
  SUPPRESS_SUCCESS_TOAST,
} from "@o2-fe/toast-message";
import Docxtemplater from "docxtemplater";
import PizZip from "pizzip";
import { Observable, from, of } from "rxjs";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";
import { LoggedUserService } from "../../core/services/logged-user.service";
import { SKIP_AUTH_HEADER } from "../../auth/constants/authenticate.constants";
import {
  DocumentLanguage,
  formatDocumentDate,
  formatLocaleCurrency,
} from "../utils/templates/document-language.utils";
import { ContractDocumentData } from "../interfaces/contract-document-data.interface";
import {
  ContractTemplateVariables,
  CustomTemplateType,
  CustomTemplateValidationError,
  CustomTemplateValidationFailure,
  CustomTemplateValidationResult,
  QuoteTemplateVariables,
  TemplatePlaceholderMetadata,
} from "../interfaces/custom-template.interface";
import { QuoteDocumentData } from "../interfaces/quote-document-data.interface";
import {
  CONTRACT_PLACEHOLDERS,
  CONTRACT_TEMPLATE_MOCK_DATA,
  QUOTE_PLACEHOLDERS,
  QUOTE_TEMPLATE_MOCK_DATA,
} from "../utils/template-mock-data.utils";
import { calculateQuoteTotals } from "../utils/templates/quote-document.utils";
import { sortQuotePriceLists } from "../utils/quote-price-list-ordering.utils";

/**
 * Human-readable labels for the contract payment mode enum values, per
 * document language. Used when building the `pagamenti` loop variables for a
 * contract template.
 */
const PAYMENT_MODE_LABELS: Record<
  DocumentLanguage,
  Record<string, string>
> = {
  it: {
    DATE: "Data",
    SAL: "SAL",
    TASK: "Task",
  },
  en: {
    DATE: "Date",
    SAL: "SAL",
    TASK: "Task",
  },
};

/**
 * Label of a vacation (T&M) level-rate row in the quote template variables,
 * per document language (e.g. `Profilo Junior — 50,00 €/h`).
 */
const VACATION_PROFILE_LABELS: Record<
  DocumentLanguage,
  (level: string, formattedRate: string) => string
> = {
  it: (level, formattedRate) => `Profilo ${level} — ${formattedRate}/h`,
  en: (level, formattedRate) => `${level} profile — ${formattedRate}/h`,
};

/**
 * Mapping from docxtemplater error codes to Italian user-facing messages.
 * Any code not in this map falls through to the docxtemplater message or a
 * generic fallback. The list is not exhaustive — it only covers the errors
 * a non-technical user can realistically produce by editing a `.docx` in Word.
 *
 * References: https://docxtemplater.com/docs/errors/
 */
const TEMPLATE_ERROR_MESSAGES: Record<string, string> = {
  unclosed_tag: "Segnaposto non chiuso (manca la parentesi graffa di chiusura)",
  unopened_tag: "Segnaposto non aperto (manca la parentesi graffa di apertura)",
  unclosed_loop:
    "Sezione ripetuta non chiusa (manca il tag di chiusura del loop)",
  unopened_loop:
    "Sezione ripetuta non aperta (manca il tag di apertura del loop)",
  closing_tag_does_not_match_opening_tag:
    "Il tag di chiusura non corrisponde a quello di apertura",
  duplicate_open_tag: "Segnaposto aperto più volte",
  duplicate_close_tag: "Segnaposto chiuso più volte",
  scopeparser_execution_failed:
    "Segnaposto non riconosciuto (probabilmente c'è un refuso)",
  scopeparser_compilation_failed: "Espressione del segnaposto non valida",
  file_has_nothing_to_parse: "Il documento è vuoto o non contiene testo",
  loop_position_invalid:
    "Posizione della sezione ripetuta non valida nel documento",
  raw_tag_outerlerr: "Tag speciale usato in una posizione non valida",
  raw_tag_should_be_only_text_in_paragraph:
    "Segnaposto immagine deve essere solo in un paragrafo dedicato",
};

/**
 * Mapping from PizZip error message substrings (English) to Italian user-facing
 * messages. PizZip is the zip parser docxtemplater wraps around — it throws
 * plain `Error` objects with English messages that would otherwise leak to the
 * user through the errors dialog. We match by substring because PizZip
 * sometimes appends position/offset info after the main message.
 */
const PIZZIP_ERROR_PATTERNS: Array<{ pattern: string; italian: string }> = [
  {
    pattern: "end of central directory",
    italian: "Il file caricato non è un documento Word valido (.docx)",
  },
  {
    pattern: "Corrupted zip",
    italian: "Il file Word è danneggiato e non può essere letto",
  },
  {
    pattern: "Invalid CRC32",
    italian: "Il file Word è danneggiato (checksum non valido)",
  },
  {
    pattern: "Encrypted zip",
    italian: "Il file Word è protetto da password: rimuovi la password e riprova",
  },
];

/**
 * Formats a numeric value as a EUR currency string according to the document
 * language (e.g. `"1.234,56 €"` for Italian, `"€1,234.56"` for English).
 * Uses the plain Intl locale style — the historical format of custom-template
 * variables (unlike the default templates, which use the `€ `-prefixed style).
 */
function formatEuro(value: number, language: DocumentLanguage): string {
  return formatLocaleCurrency(value, language);
}

/**
 * Service responsible for everything related to tenant custom `.docx` document
 * templates:
 *
 * - **Validation**: render the uploaded template with mock data via docxtemplater
 *   to surface malformed placeholders (sintax errors), AND extract the set of
 *   placeholders the user actually used to detect "expected but missing" ones.
 * - **Upload**: two-step presigned POST flow (get URL from backend, POST directly
 *   to S3 with FormData). Mirrors the pattern used by `attachment-table.component`.
 * - **Download + render**: two-step presigned GET flow, then render the template
 *   with real quote/contract data and return a Blob ready for `saveAs`.
 * - **Variable mapping**: translate the app's internal {@link QuoteDocumentData}
 *   and {@link ContractDocumentData} shapes into the **Italian, user-friendly**
 *   placeholder dictionary the user's template expects.
 */
@Injectable({ providedIn: "root" })
export class CustomTemplateService {
  private readonly _tenantsApi = inject(UsersTenantsService);
  private readonly _translationsApi = inject(TranslationsCustomTemplatesService);
  private readonly _httpClient = inject(HttpClient);
  private readonly _loggedUserService = inject(LoggedUserService);

  /**
   * Validates a user-uploaded `.docx` template:
   *  1. Renders it with mock data via docxtemplater + image module to catch
   *     any syntax errors (malformed placeholders, unclosed loops, etc.)
   *     AND any unknown-placeholder errors — the `nullGetter` is configured
   *     in strict mode, so a tag the user introduced with a typo that is
   *     not in the mock data triggers an error instead of silently
   *     rendering as an empty string.
   *  2. If syntax is fine, scans the template for the placeholders it
   *     actually contains, and computes which canonical placeholders the
   *     user has removed. These are returned as `missingPlaceholders` in the
   *     result — they are NOT errors, just a list the caller can surface as
   *     a non-blocking warning.
   *
   * On a syntax failure throws a {@link CustomTemplateValidationFailure}.
   * On success emits a {@link CustomTemplateValidationResult}.
   */
  public validateTemplate(
    file: File,
    templateType: CustomTemplateType,
  ): Observable<CustomTemplateValidationResult> {
    return from(file.arrayBuffer()).pipe(
      map((buffer) => this.validateTemplateBuffer(buffer, templateType)),
    );
  }

  /**
   * Buffer-based variant of {@link validateTemplate}, shared with the flows
   * that already hold the raw `.docx` bytes (e.g. validation of the
   * auto-translated English template after download from S3).
   *
   * @param buffer - Raw bytes of the `.docx` template.
   * @param templateType - Which template type the buffer represents.
   * @returns The validation result with the missing-placeholders list.
   * @throws {CustomTemplateValidationFailure} On any placeholder syntax error.
   */
  public validateTemplateBuffer(
    buffer: ArrayBuffer,
    templateType: CustomTemplateType,
  ): CustomTemplateValidationResult {
    const presentPlaceholders = this._extractPresentPlaceholders(buffer);
    try {
      const zip = new PizZip(buffer);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        // Strict mode: any placeholder not present in the mock data throws
        // a scopeparser_execution_failed error, which we catch and surface
        // through the errors dialog. Without this, typos like {nome_clente}
        // would silently render as empty strings.
        nullGetter: (part: { value?: string; module?: string }) => {
          const tag = part.value || part.module || "unknown";
          throw new Error(`Segnaposto sconosciuto: ${tag}`);
        },
      });
      const mockData =
        templateType === "quote"
          ? QUOTE_TEMPLATE_MOCK_DATA
          : CONTRACT_TEMPLATE_MOCK_DATA;
      doc.render(mockData as unknown as Record<string, unknown>);
    } catch (error) {
      throw this._buildValidationFailure(error);
    }

    const expected = this.getCanonicalPlaceholders(templateType);
    const missingPlaceholders = expected
      .map((p) => p.key)
      .filter((key) => !presentPlaceholders.has(key));

    return { missingPlaceholders };
  }

  /**
   * Returns the canonical (full) set of placeholder metadata for a given
   * template type, used both by the validation result and by the reference
   * dialog. Pure read of the static catalog from `template-mock-data.utils`.
   */
  public getCanonicalPlaceholders(
    templateType: CustomTemplateType,
  ): TemplatePlaceholderMetadata[] {
    return templateType === "quote"
      ? QUOTE_PLACEHOLDERS
      : CONTRACT_PLACEHOLDERS;
  }

  /**
   * Uploads a validated `.docx` template to S3 via the two-step presigned
   * POST flow (presigned URL from backend → fetch POST multipart to S3).
   */
  public uploadTemplate(
    file: File,
    templateType: CustomTemplateType,
    tenantName: string,
    context?: HttpContext,
  ): Observable<string> {
    return this._tenantsApi
      .tenantsControllerGenerateCustomTemplateUploadUrl(
        {
          name: tenantName,
          body: {
            templateType,
            fileSize: file.size,
          },
        },
        context,
      )
      .pipe(
        switchMap((response) => {
          const formData = new FormData();
          const fields = response.fields ?? {};
          Object.entries(fields).forEach(([key, value]) => {
            formData.append(key, value as string);
          });
          formData.append("file", file);

          return from(
            fetch(response.presignedUrl, {
              method: "POST",
              body: formData,
            }),
          ).pipe(
            tap((res) => {
              if (!res.ok) {
                throw new Error(
                  `S3 upload failed with status ${res.status} ${res.statusText}`,
                );
              }
            }),
            map(() => response.s3Key),
          );
        }),
      );
  }

  /**
   * Downloads a custom template from S3 and renders it with the provided
   * variables, returning a Blob ready to save.
   */
  public generateDocument(
    templateS3Key: string,
    variables: Record<string, unknown>,
    tenantName: string,
  ): Observable<Blob> {
    return this.downloadTemplateBuffer(templateS3Key, tenantName).pipe(
      map((buffer) => this._renderDocument(buffer, variables)),
    );
  }

  /**
   * Downloads a custom template from S3 (two-step presigned GET flow) and
   * returns its raw bytes. Shared by rendering and by the validation of the
   * auto-translated English template.
   *
   * @param templateS3Key - Full S3 key of the template.
   * @param tenantName - Tenant name/slug for the presigned URL endpoint.
   * @returns The template content as an ArrayBuffer.
   */
  public downloadTemplateBuffer(
    templateS3Key: string,
    tenantName: string,
    context?: HttpContext,
  ): Observable<ArrayBuffer> {
    return this._tenantsApi
      .tenantsControllerGenerateCustomTemplateDownloadUrl(
        {
          name: tenantName,
          s3Key: templateS3Key,
        },
        context,
      )
      .pipe(
        switchMap((response) =>
          this._httpClient.get(response.presignedUrl, {
            responseType: "blob",
            headers: new HttpHeaders({ [SKIP_AUTH_HEADER]: "true" }),
          }),
        ),
        switchMap((blob: Blob) => from(blob.arrayBuffer())),
      );
  }

  /**
   * Resolves the S3 key of the English version of a tenant custom template,
   * translating it lazily when it does not exist yet.
   *
   * Flow:
   * 1. If the tenant already carries the English key
   *    (`quoteTemplateEnS3Key` / `contractTemplateEnS3Key`), return it.
   * 2. Otherwise ask the backend to translate the Italian template via AWS
   *    Translate (deterministic `_en` key, idempotent on retry).
   * 3. Download the translated template and validate its placeholder
   *    integrity (machine translation may mangle docxtemplater tags).
   * 4. On success, persist the key on the tenant (lazy migration) so future
   *    English downloads skip the translation entirely. A persistence failure
   *    (e.g. insufficient permissions) is non-blocking: the key is still used
   *    for the current download.
   *
   * Emits `null` when no English template can be produced (no Italian
   * template configured, translation error, or broken placeholders) — the
   * caller is expected to fall back to the default English template.
   *
   * @param tenant - Tenant carrying the template configuration.
   * @param templateType - Which template to resolve (quote or contract).
   * @returns The English template S3 key, or `null` when unavailable.
   */
  public resolveEnglishTemplateKey(
    tenant: UsersTenantResponseDto,
    templateType: CustomTemplateType,
  ): Observable<string | null> {
    const englishKey =
      templateType === "quote"
        ? tenant.quoteTemplateEnS3Key
        : tenant.contractTemplateEnS3Key;
    if (englishKey) {
      return of(englishKey);
    }

    const sourceKey =
      templateType === "quote"
        ? tenant.quoteTemplateS3Key
        : tenant.contractTemplateS3Key;
    if (!sourceKey) {
      return of(null);
    }

    // Suppress both interceptor toasts on the persistence PATCH: a failure
    // (e.g. insufficient permissions) is non-blocking by design.
    const suppressToasts = new HttpContext()
      .set(SUPPRESS_SUCCESS_TOAST, true)
      .set(SUPPRESS_ERROR_TOAST, true);

    return this.translateTemplateToEnglish(
      tenant.name,
      sourceKey,
      templateType,
    ).pipe(
      switchMap((validKey) => {
        if (!validKey) {
          return of(null);
        }
        return this._persistEnglishTemplateKey(
          tenant,
          templateType,
          sourceKey,
          validKey,
          suppressToasts,
        );
      }),
    );
  }

  /**
   * Translates a custom template to English via the backend (AWS Translate)
   * and validates the placeholder integrity of the result.
   *
   * Emits the S3 key of the validated English template, or `null` when the
   * translation could not be produced (backend error, invalid docx, or
   * placeholders mangled by the machine translation). Never errors — failures
   * are logged and mapped to `null` so callers only deal with the fallback.
   *
   * @param tenantName - Tenant name/slug.
   * @param sourceKey - S3 key of the source (Italian) template.
   * @param templateType - Which template is being translated.
   * @returns The English template S3 key, or `null` when unavailable.
   */
  public translateTemplateToEnglish(
    tenantName: string,
    sourceKey: string,
    templateType: CustomTemplateType,
  ): Observable<string | null> {
    // Suppress both interceptor toasts: this flow has its own curated
    // fallback messaging and must never surface raw backend errors.
    const suppressToasts = new HttpContext()
      .set(SUPPRESS_SUCCESS_TOAST, true)
      .set(SUPPRESS_ERROR_TOAST, true);

    return this._translationsApi
      .customTemplatesControllerTranslateCustomTemplate(
        {
          name: tenantName,
          body: { s3Key: sourceKey, templateType },
        },
        suppressToasts,
      )
      .pipe(
        switchMap((response) =>
          this.downloadTemplateBuffer(
            response.s3Key,
            tenantName,
            suppressToasts,
          ).pipe(
            map((buffer) => {
              // Throws CustomTemplateValidationFailure when the machine
              // translation broke any placeholder — caught below as null.
              this.validateTemplateBuffer(buffer, templateType);
              return response.s3Key;
            }),
          ),
        ),
        catchError((error: unknown) => {
          console.error(
            `English ${templateType} template translation failed:`,
            error,
          );
          return of(null);
        }),
      );
  }

  /**
   * Persists the freshly validated English template key on the tenant (lazy
   * migration) and syncs the logged-user tenant state. Persistence errors are
   * swallowed: the key is still valid for the current download and the next
   * one will simply re-translate to the same deterministic key.
   *
   * Guarded by a freshness check: the translate → download → validate chain is
   * async, so the source template may have been removed or replaced (from the
   * tenant config sheet) while it was in flight. Persisting unconditionally
   * would then revive a stale English key pointing at an orphaned template
   * (source key `null` but English key set). If the live tenant's source key no
   * longer matches the one we translated from, the persist is skipped — the
   * current download still uses the resolved key, but nothing stale is written.
   *
   * @param tenant - Tenant to update.
   * @param templateType - Which template field to set.
   * @param sourceKey - Source (Italian) key this English key was derived from.
   * @param englishKey - Validated English template S3 key.
   * @param context - HTTP context (success toast suppression).
   * @returns The English key, regardless of persistence outcome.
   */
  private _persistEnglishTemplateKey(
    tenant: UsersTenantResponseDto,
    templateType: CustomTemplateType,
    sourceKey: string,
    englishKey: string,
    context: HttpContext,
  ): Observable<string> {
    const fieldName =
      templateType === "quote"
        ? "quoteTemplateEnS3Key"
        : "contractTemplateEnS3Key";
    const sourceField =
      templateType === "quote"
        ? "quoteTemplateS3Key"
        : "contractTemplateS3Key";

    return this._loggedUserService.tenantSubject$.pipe(
      take(1),
      switchMap((currentTenant) => {
        // Freshness guard: skip the persist if the source template changed or
        // was removed while this resolution was in flight.
        if (!currentTenant || currentTenant[sourceField] !== sourceKey) {
          return of(englishKey);
        }

        return this._tenantsApi
          .tenantsControllerUpdate(
            { id: tenant.id, body: { [fieldName]: englishKey } },
            context,
          )
          .pipe(
            map((updatedTenant) => {
              this._loggedUserService.updateTenant(updatedTenant);
              return englishKey;
            }),
            catchError((error: unknown) => {
              console.warn(
                "Could not persist the English template key on the tenant (the key is still used for this download):",
                error,
              );
              return of(englishKey);
            }),
          );
      }),
    );
  }

  /**
   * Maps a {@link QuoteDocumentData} into the Italian flat dictionary expected
   * by a quote template. Currency values are pre-formatted as EUR strings.
   * Empty strings are used for optional fields to keep the template rendering
   * predictable regardless of tenant configuration.
   *
   * Both `voci` and `voci_vacazione` arrays are always passed — one of the
   * two will typically be empty depending on `quote.compensationMode`, and
   * docxtemplater simply omits the loop rows for the empty array.
   */
  public buildQuoteVariables(data: QuoteDocumentData): QuoteTemplateVariables {
    const language = data.language;
    const totals = calculateQuoteTotals(data.quote, data.tenant);
    const complexityMultiplier = data.quote.complexity || 1;
    const vacationRows = this._buildVacationRows(data);

    return {
      codice_preventivo: data.quote.code ?? "",
      nome_preventivo: data.quote.name ?? "",
      data_generazione: data.generatedDate ?? "",

      nome_cliente: data.customerName ?? "",
      piva_cliente: data.customerVat ?? "",

      nome_azienda: data.tenant.companyName ?? "",
      indirizzo_azienda: data.tenant.address?.street ?? "",
      citta_azienda: data.tenant.address?.city ?? "",
      provincia_azienda: data.tenant.address?.province ?? "",
      cap_azienda: data.tenant.address?.postalCode ?? "",
      piva_azienda: data.tenant.vatNumber ?? "",
      sdi_azienda: data.tenant.sdi ?? "",
      email_azienda: data.tenant.email ?? "",
      telefono_azienda: data.tenant.phone ?? "",

      subtotale: formatEuro(totals.subtotal, language),
      sconto:
        totals.discount !== undefined
          ? formatEuro(totals.discount, language)
          : "",
      inarcassa:
        totals.inarcassa !== undefined
          ? formatEuro(totals.inarcassa, language)
          : "",
      iva: formatEuro(totals.iva, language),
      totale: formatEuro(totals.total, language),

      ha_voci: (data.priceLists ?? []).length > 0,

      // Items follow the shared display ordering (persisted order fields
      // with alphanumeric fallback), the same rules used by the quote sheet
      // and the default template.
      voci: sortQuotePriceLists(data.priceLists ?? []).map((item) => {
        const basePrice = item.price || 0;
        const complexitiesSum = (item.complexities || []).reduce(
          (sum, c) => sum + (c.price || 0),
          0,
        );
        const finalPrice = (basePrice + complexitiesSum) * complexityMultiplier;
        return {
          nome_voce: item.name ?? "",
          descrizione_voce: item.description ?? "",
          categoria_voce: item.category?.name ?? "",
          prezzo_voce: formatEuro(finalPrice, language),
        };
      }),

      ha_voci_vacazione: vacationRows.length > 0,

      voci_vacazione: vacationRows,
    };
  }

  /**
   * Builds the `voci_vacazione` rows for a quote template.
   *
   * Modern VACATION quotes carry per-level hourly rates (`levelRates`): one row
   * per junior/mid/senior level that has estimated hours, showing the hourly
   * rate and the `rate × estimatedHours` line total. Legacy quotes still using
   * `vacationItems` fall back to the old object/hours/price mapping.
   */
  private _buildVacationRows(
    data: QuoteDocumentData,
  ): QuoteTemplateVariables["voci_vacazione"] {
    const language = data.language;
    const levelRates = data.quote.levelRates;
    if (data.quote.compensationMode === "VACATION" && levelRates) {
      const levels: Array<[string, SalesLevelRateEntryDto | undefined]> = [
        ["Junior", levelRates.junior],
        ["Mid", levelRates.mid],
        ["Senior", levelRates.senior],
      ];
      return levels
        .filter(([, entry]) => (entry?.estimatedHours ?? 0) > 0)
        .map(([label, entry]) => {
          const rate = entry?.rate ?? 0;
          const hours = entry?.estimatedHours ?? 0;
          return {
            descrizione_voce: VACATION_PROFILE_LABELS[language](
              label,
              formatEuro(rate, language),
            ),
            ore: String(hours),
            prezzo_voce: formatEuro(rate * hours, language),
          };
        });
    }

    return (data.vacationItems ?? []).map((item) => ({
      descrizione_voce: item.object ?? "",
      ore: String(item.quantity ?? 0),
      prezzo_voce: formatEuro(
        (item.hourlyCost || 0) * (item.quantity || 0),
        language,
      ),
    }));
  }

  /**
   * Maps a {@link ContractDocumentData} into the Italian flat dictionary for
   * a contract template.
   *
   * The `pagamenti` loop covers the three `paymentMode` variants with a single
   * row schema: `scadenza` is the actual due date for DATE, the formatted
   * percentage for SAL, and the associated task name for TASK.
   */
  public buildContractVariables(
    data: ContractDocumentData,
  ): ContractTemplateVariables {
    const language = data.language;
    const amount = data.contract.amount || 0;
    const discount = data.contract.discount || 0;
    const total = amount - discount;

    return {
      codice_contratto: data.contract.code ?? "",
      nome_contratto: data.contract.name ?? "",
      data_firma: data.signedDate ?? "",
      data_generazione: data.generatedDate ?? "",

      is_vacazione: data.contract.compensationMode === "VACATION",

      nome_cliente: data.customerName ?? "",
      piva_cliente: data.customerVat ?? "",
      indirizzo_cliente: data.customerAddress ?? "",
      telefono_cliente: data.customerPhone ?? "",
      email_cliente: data.customerEmail ?? "",

      nome_azienda: data.tenant.companyName ?? "",
      indirizzo_azienda: data.tenant.address?.street ?? "",
      citta_azienda: data.tenant.address?.city ?? "",
      provincia_azienda: data.tenant.address?.province ?? "",
      cap_azienda: data.tenant.address?.postalCode ?? "",
      piva_azienda: data.tenant.vatNumber ?? "",
      email_azienda: data.tenant.email ?? "",
      compagnia_assicurativa: data.tenant.insuranceCompany ?? "",
      numero_polizza: data.tenant.policyNumber ?? "",

      importo: formatEuro(amount, language),
      sconto: discount > 0 ? formatEuro(discount, language) : "",
      totale: formatEuro(total, language),

      pagamenti: (data.payments ?? []).map((payment) => {
        const paymentAmount = formatEuro(payment.amount || 0, language);
        const mode = payment.paymentMode || "";
        const modeLabel = PAYMENT_MODE_LABELS[language][mode] || mode;

        let scadenzaField = "";
        if (mode === "DATE" && payment.dueDate) {
          const date = new Date(payment.dueDate);
          scadenzaField = isNaN(date.getTime())
            ? payment.dueDate
            : formatDocumentDate(date, language);
        } else if (mode === "SAL" && payment.salPercentage !== undefined) {
          scadenzaField = `${payment.salPercentage}%`;
        } else if (mode === "TASK" && payment.priceList?.name) {
          scadenzaField = payment.priceList.name;
        }

        return {
          importo: paymentAmount,
          modalita: modeLabel,
          scadenza: scadenzaField,
          note: payment.notes ?? "",
        };
      }),
    };
  }

  /**
   * Renders a loaded `.docx` zip with docxtemplater and returns the resulting
   * Blob. Shared by `generateDocument` and (indirectly) by `validateTemplate`
   * via the mock data path.
   */
  private _renderDocument(
    templateBuffer: ArrayBuffer,
    variables: Record<string, unknown>,
  ): Blob {
    const zip = new PizZip(templateBuffer);
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
    });
    doc.render(variables);
    return doc.getZip().generate({
      type: "blob",
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
  }

  /**
   * Extracts the set of placeholder names actually present in a `.docx` file.
   *
   * Scans **all** XML files inside the zip that can contain document text
   * (body, headers, footers). docxtemplater's `getFullText()` only returns
   * the body text — it does NOT include the content of Word Page Headers /
   * Footers. Since the example templates use real Word headers for the
   * logo + date + email block, we must inspect those XML parts too,
   * otherwise the warning dialog would falsely flag `data_generazione`
   * and `email_azienda` as missing even when they are correctly placed.
   *
   * For each XML part we strip the tags (which collapses `<w:t>` runs so
   * placeholders split across multiple runs by Word become a single string)
   * and apply a regex that catches:
   *  - `{name}`     → text placeholders
   *  - `{#name}`    → loop / section opening tags
   *  - `{^name}`    → inverted section opening tags
   *  - `{/name}`    → loop / section closing tags
   *  - `{%name}`    → image module placeholders
   *
   * Returns a Set of canonical names (without braces, prefixes or slashes).
   */
  private _extractPresentPlaceholders(buffer: ArrayBuffer): Set<string> {
    const present = new Set<string>();
    try {
      const zip = new PizZip(buffer);
      const tagRegex = /\{(?:[#/%^])?([a-zA-Z0-9_.]+)\}/g;

      const relevantFiles = Object.keys(zip.files).filter(
        (name) =>
          name.endsWith(".xml") &&
          (name === "word/document.xml" ||
            (name.startsWith("word/header") && name.endsWith(".xml")) ||
            (name.startsWith("word/footer") && name.endsWith(".xml"))),
      );

      for (const fileName of relevantFiles) {
        const file = zip.file(fileName);
        if (!file) continue;
        // Strip XML tags so that placeholders split across multiple <w:t>
        // runs become a contiguous string we can regex-match cleanly.
        const cleanText = file.asText().replace(/<[^>]+>/g, "");
        let match: RegExpExecArray | null;
        tagRegex.lastIndex = 0;
        while ((match = tagRegex.exec(cleanText)) !== null) {
          present.add(match[1]);
        }
      }
    } catch {
      // If we cannot extract (corrupted file, etc.), fall through with empty set.
      // The render-based validation step will catch and surface the real error.
    }
    return present;
  }

  /**
   * Parses a docxtemplater thrown error into an array of structured validation
   * errors. Preserves every underlying error (no arbitrary cap) and translates
   * known error codes to Italian via {@link TEMPLATE_ERROR_MESSAGES}.
   *
   * Detects PizZip errors (thrown before docxtemplater even runs, e.g. when
   * the uploaded file is not a valid zip) and maps them through
   * {@link PIZZIP_ERROR_PATTERNS} so the user sees an Italian message rather
   * than the raw English "Can't find end of central directory : is this a zip file ?".
   */
  private _parseTemplateErrors(
    error: unknown,
  ): CustomTemplateValidationError[] {
    const fallback: CustomTemplateValidationError = {
      code: "unknown",
      italianMessage: "Errore sconosciuto durante la convalida del modello",
    };

    if (!error || typeof error !== "object") {
      return [fallback];
    }

    const err = error as {
      message?: string;
      properties?: {
        errors?: Array<{
          message?: string;
          properties?: {
            id?: string;
            xtag?: string;
            context?: string;
            explanation?: string;
          };
        }>;
      };
    };

    const details = err.properties?.errors;
    if (Array.isArray(details) && details.length > 0) {
      return details.map((detail) => {
        const code = detail.properties?.id ?? "unknown";
        const italianMessage =
          TEMPLATE_ERROR_MESSAGES[code] ??
          detail.properties?.explanation ??
          detail.message ??
          "Errore nel segnaposto";
        return {
          code,
          italianMessage,
          tagName: detail.properties?.xtag,
          context: detail.properties?.context,
        };
      });
    }

    // No structured docxtemplater errors: check for known PizZip messages
    // (file is not a valid zip / .docx) and map them to Italian.
    const rawMessage = err.message ?? "";
    for (const { pattern, italian } of PIZZIP_ERROR_PATTERNS) {
      if (rawMessage.includes(pattern)) {
        return [
          {
            code: "invalid_docx_file",
            italianMessage: italian,
          },
        ];
      }
    }

    return [
      {
        ...fallback,
        italianMessage: rawMessage || fallback.italianMessage,
      },
    ];
  }

  /**
   * Wraps a raw docxtemplater error into a typed validation failure exposing
   * the structured `errors` array, so the parent component can open a dialog
   * listing every problem.
   */
  private _buildValidationFailure(
    error: unknown,
  ): CustomTemplateValidationFailure {
    const errors = this._parseTemplateErrors(error);
    const wrapped = new Error(
      "Template validation failed",
    ) as CustomTemplateValidationFailure;
    wrapped.errors = errors;
    return wrapped;
  }
}
